import { html, nothing, ifDefined } from 'omni-ui';
import { Om2FormFieldBase, SHARED_FIELD } from './common/base.js';
import { Om2Dropdown } from '../om2-dropdown.js';

Om2Dropdown.register();

/**
 * Render enum as a dropdown input
 */
export class Om2FormFieldDropdown extends Om2FormFieldBase {
  static metadata = {
    config: {
      label: 'Dropdown',
      overrides: {
        type: 'string',
      },
      fields: {
        id: SHARED_FIELD,
        title: SHARED_FIELD,
        asyncEnum: {
          title: 'External Options (Asynchronous)',
          type: 'boolean',
          default: false,
        },
        enum: {
          type: 'array',
          id: 'enum',
          title: 'Options',
          minRows: 1,
          content: [
            {
              type: 'column',
              content: [
                { type: 'ref', key: 'id', id: '__array_id', required: true },
              ],
            },
            {
              type: 'column',
              content: [{ type: 'ref', key: 'name', id: '__array_name' }],
            },
          ],
          removeButton: {
            class: 'is-medium no-border-background is-shadowless is-text',
            icon: {
              id: 'omni:interactive:remove',
              class: 'is-size-2',
            },
          },
          addButton: {
            class: 'is-outlined',
            icon: {
              id: 'omni:interactive:add',
              class: 'is-size-2',
            },
          },
          formModal: {
            fields: {
              __array_id: {
                type: 'string',
                title: 'Id',
                tooltip:
                  'The id uniquely identifies the option within the dropdown and should not change.',
              },
              __array_name: {
                type: 'string',
                title: 'Name',
                tooltip:
                  'This is the "pretty" name that is displayed to the user',
              },
            },
            hideIf: { id: 'asyncEnum', op: '===', value: true },
            fullWidth: true,
          },
        },
        treeView: {
          title: 'Hierarchical/Tree View',
          type: 'boolean',
          default: false,
          tooltip:
            'Renders the options in a nested tree view. The Options URL should contain JSON with each option potentially containing a "children" key with an array of child options.',
          formModal: {
            hideIf: { id: 'asyncEnum', op: '===', value: false },
          },
        },
        enumURL: {
          title: 'Options URL',
          type: 'string',
          tooltip:
            'The URL from which to load options asyncronously. It must return properly formatted JSON or CSV with "id" and "name" keys/columns. To reference an external field (id = "my_field") in the url, use the syntax: %:my_field%',
          formModal: {
            hideIf: { id: 'asyncEnum', op: '===', value: false },
            fullWidth: true,
          },
        },
        tooltip: SHARED_FIELD,
        class: SHARED_FIELD,
        default: {
          title: 'Default',
          type: 'string',
          tooltip: 'The id of the default option',
        },
        placeholder: SHARED_FIELD,
        'custom_input.name': {
          title: 'Custom Input Option',
          type: 'string',
          tooltip:
            'The option name to add to the dropdown to enable "Custom Inputs"',
        },
        'custom_input.placeholder': {
          title: 'Custom Input Placeholder',
          type: 'string',
          tooltip:
            'Placeholder for the input field when the custom input option is selected',
        },
        isMulti: {
          title: 'Multiselect',
          type: 'boolean',
          tooltip:
            'Allow selecting multiple options, incompatible with custom input',
        },
        hasSearch: {
          title: 'Has Search',
          type: 'boolean',
          tooltip: 'Add search functionality to dropdown',
        },
        required: SHARED_FIELD,
        disabled: SHARED_FIELD,
        readonly: {
          title: 'Read Only',
          type: 'boolean',
          tooltip: "Rendered as disabled since the dropdown can't be readonly",
        },
        hasObjectValues: SHARED_FIELD,
      },
    },
  };

  render() {
    const { schema } = this;
    let value = this.data ?? schema.default ?? undefined;
    let options = schema.enum;
    const customId = '____custom'; // This id isnt saved, just used internally
    let customPrefix;
    let customLabel;
    let customValue;
    const changeHandler = (e, opts = {}) =>
      this.dispatchNewEvent('input', {
        detail: {
          name: this.key,
          value: e.target.value === customId ? customPrefix : e.target.value,
          ...opts,
        },
      });

    if (schema.custom_input) {
      customPrefix = schema.custom_input?.id_prefix ?? 'Other: ';
      customLabel = schema.custom_input?.name ?? 'Other';
      options = [...options, { id: customId, name: customLabel }];
      if (value?.length && value.startsWith && value.startsWith(customPrefix)) {
        customValue = value.substring(customPrefix.length);
        value = customId;
      }
    }

    return html`<om2-dropdown
        .options=${schema.treeView ? nothing : options}
        .optionsTree=${schema.treeView ? options : nothing}
        .name="${this.key}"
        .value=${value}
        .placeholder=${schema.placeholder ?? ''}
        .asyncSearch=${schema.asyncSearch}
        .allowClear=${!!schema.allowClear}
        .hasObjectValues=${schema.hasObjectValues}
        .renderOption=${schema.renderOption ?? nothing}
        .renderSelected=${schema.renderSelected ?? nothing}
        ?isMulti=${schema.isMulti}
        ?hasSearch=${schema.hasSearch}
        ?required=${schema.required}
        ?disabled=${schema.disabled ||
        schema.readonly ||
        this.context?.readonly ||
        this._isHidden()}
        class="has-text-weight-normal"
        idProperty=${schema.idProperty ?? 'id'}
        nameProperty=${schema.nameProperty ?? 'name'}
        childrenProperty=${schema.childrenProperty ?? nothing}
        @change=${changeHandler}
        @init-change=${e => changeHandler(e, { isAutoGenerated: true })}
      >
      </om2-dropdown>
      ${customValue !== undefined
        ? html`
            <input
              class="input mt-1"
              .value=${customValue}
              placeholder=${ifDefined(schema.custom_input.placeholder)}
              ?disabled=${schema.disabled || this._isHidden()}
              ?readonly=${schema.readonly || this.context?.readonly}
              @input=${e => {
                e.stopPropagation();
                this.dispatchNewEvent('input', {
                  detail: {
                    name: this.key,
                    value: customPrefix + e.target.value,
                  },
                });
              }}
            />
          `
        : null}`;
  }
}

Om2FormFieldDropdown.register('dropdown');
